/**
 * CmsLayoutsPage — Named Layouts list
 *
 * Lists all named layout configurations for a space.
 * Each layout defines contentWidth, headerStyle, navPosition, footerStyle
 * and which nav menus to use in the header/footer.
 * Clicking a layout opens its editor.
 */

import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { LayoutTemplate, Plus, Trash2, ChevronRight, Star } from 'lucide-react';
import { api } from '../lib/api.ts';
import { extractError } from '../lib/types.ts';
import { PageHeader } from '../components/ui/PageHeader.tsx';
import { Button } from '../components/ui/Button.tsx';
import { Badge } from '../components/ui/Badge.tsx';
import { Modal, ModalFooter } from '../components/ui/Modal.tsx';
import { Input } from '../components/ui/Input.tsx';
import { EmptyState } from '../components/ui/EmptyState.tsx';
import { SkeletonRows } from '../components/ui/Skeleton.tsx';
import { ConfirmDialog } from '../components/ui/ConfirmDialog.tsx';
import { useToast } from '../components/ui/Toast.tsx';

// ─── Types ─────────────────────────────────────────────────────────────────────

interface CmsLayout {
  id: string;
  name: string;
  slug: string;
  config: Record<string, unknown>;
  isDefault: boolean;
  headerMenuSlug: string | null;
  footerMenuSlug: string | null;
  createdAt: string;
  updatedAt: string;
}

// ─── Page ──────────────────────────────────────────────────────────────────────

export function CmsLayoutsPage() {
  const { spaceId } = useParams<{ spaceId: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();

  const BASE = `/api/v1/cms/spaces/${spaceId}/layouts`;

  const [showCreate, setShowCreate] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<CmsLayout | null>(null);
  const [name, setName] = useState('');

  // ── Queries ─────────────────────────────────────────────────────────────────

  const { data: layouts = [], isLoading } = useQuery<CmsLayout[]>({
    queryKey: ['cms-layouts', spaceId],
    queryFn: () => api.get(BASE).then((r) => r.data),
    enabled: !!spaceId,
  });

  // ── Mutations ────────────────────────────────────────────────────────────────

  const createMutation = useMutation({
    mutationFn: (layoutName: string) =>
      api.post(BASE, { name: layoutName }).then((r) => r.data as CmsLayout),
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: ['cms-layouts', spaceId] });
      setShowCreate(false);
      setName('');
      navigate(`/spaces/${spaceId}/layouts/${created.id}`);
    },
    onError: (err) => toast.error(extractError(err, 'Failed to create layout')),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`${BASE}/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cms-layouts', spaceId] });
      setDeleteTarget(null);
      toast.success('Layout deleted');
    },
    onError: (err) => toast.error(extractError(err)),
  });

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <div>
      <PageHeader
        title="Layouts"
        description="Define named layout configurations — content width, header style, and which menus to show."
        action={
          <Button onClick={() => setShowCreate(true)} size="sm">
            <Plus className="w-3.5 h-3.5 mr-1.5" /> New Layout
          </Button>
        }
      />

      {isLoading ? (
        <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
          <SkeletonRows count={3} cols={3} />
        </div>
      ) : layouts.length === 0 ? (
        <EmptyState
          icon={<LayoutTemplate size={26} />}
          title="No layouts yet"
          description="Create a layout to control how pages appear — content width, header, footer, and menus"
          action={{ label: 'New Layout', onClick: () => setShowCreate(true) }}
        />
      ) : (
        <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden divide-y divide-zinc-100">
          {layouts.map((layout) => (
            <button
              key={layout.id}
              onClick={() => navigate(`/spaces/${spaceId}/layouts/${layout.id}`)}
              className="flex items-center justify-between px-5 py-4 w-full text-left hover:bg-zinc-50 transition-colors group"
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-zinc-100 text-zinc-500 group-hover:bg-zinc-900 group-hover:text-white transition-colors flex-shrink-0">
                  <LayoutTemplate size={15} />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-zinc-900">{layout.name}</span>
                    {layout.isDefault && (
                      <Badge variant="success">
                        <Star size={9} className="mr-0.5" /> Default
                      </Badge>
                    )}
                  </div>
                  <div className="text-xs text-zinc-400 font-mono mt-0.5">{layout.slug}</div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {!layout.isDefault && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeleteTarget(layout);
                    }}
                    className="p-1.5 text-zinc-300 hover:text-red-500 hover:bg-red-50 rounded-md opacity-0 group-hover:opacity-100 transition-opacity"
                    title="Delete layout"
                  >
                    <Trash2 size={13} />
                  </button>
                )}
                <ChevronRight size={15} className="text-zinc-300" />
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Create Modal */}
      <Modal
        open={showCreate}
        onClose={() => {
          setShowCreate(false);
          setName('');
        }}
        title="New Layout"
      >
        <div className="space-y-4">
          <Input
            label="Name *"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Default Layout"
            autoFocus
          />
        </div>

        {createMutation.isError && (
          <p className="text-xs text-red-500 mt-3">{extractError(createMutation.error)}</p>
        )}

        <ModalFooter>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setShowCreate(false);
              setName('');
            }}
          >
            Cancel
          </Button>
          <Button
            loading={createMutation.isPending}
            disabled={!name.trim()}
            onClick={() => createMutation.mutate(name)}
          >
            Create & Edit
          </Button>
        </ModalFooter>
      </Modal>

      {/* Delete confirm */}
      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete layout?"
        message={`"${deleteTarget?.name}" will be permanently deleted. Pages using it will fall back to the default layout.`}
        confirmLabel="Delete"
        variant="danger"
        loading={deleteMutation.isPending}
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
